import { Subscription, EMPTY, Subject } from 'rxjs';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { FullStory, init } from '@fullstory/browser';
import { PublicLogLevel } from '@fullstory/snippet';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Intercom } from 'ng-intercom';
import { versions } from 'src/environments/versions';
import { environment } from '../../environments/environment';
import { UserData } from '../models/user';
import { NetworkEvent } from '../common/interfaces/rtc-interface';
import { DEFAULT_LANG_CODE } from '../models/localization.model';
import { InspectModeRepository } from '../state/inspect-mode-repository.service';
import {
  CountedMetric,
  ExternalMetric,
  PerformanceLogger,
  ScenarioRecord,
} from './performance.logger.service';
import { NavigatorConnectionTarget } from './network.service';

/* Wrapper around FullStory API to add addition functionality like sessionVars */

@Injectable({
  providedIn: 'root',
})
export class TelemetryService implements OnDestroy {
  // Replay the last event to late subscribers so `mostRecentEvent$` matches its name.
  private readonly mostRecentEventSubject = new Subject<string>();
  public readonly mostRecentEvent$ = this.mostRecentEventSubject.asObservable();
  private perfLogger!: PerformanceLogger;
  private readonly INTERCOM_EVENTS_WHITE_LIST = [
    /space-manager-loaded/,
    /spaces_did_load/,
    /joined_or_started_call/,
    /spaceLoaded/,
    /techcheck_step_progressed/,
    /techcheck_device_benchmark_did_complete/,
    /techcheck_network_benchmark_did_complete/,
    /user_did_upgrade/,
    /user_did_switch/,
    /user_did_cancel/,
    /user_role_data_provided/,
  ];

  // Listen to Performance Scenario Completions (to get Perf metrics for specific scenrios)
  public readonly perfScenarioCompleted$: Observable<ScenarioRecord>;

  // Listen to latest Performance Metrics (to get general Perf snapshot)
  public readonly currPerfMetrics$: Observable<Record<string, any>>;

  // Track subscription for cleanup
  private perfScenarioSubscription?: Subscription;

  // Track beforeunload listener for cleanup
  private beforeUnloadListener?: () => void;

  constructor(
    private readonly intercom: Intercom,
    private readonly inspectModeRepository: InspectModeRepository,
    injector: Injector,
  ) {
    if (this.inspectModeRepository.isInspectMode) {
      this.perfScenarioCompleted$ = EMPTY;
      this.currPerfMetrics$ = EMPTY as unknown as Observable<Record<string, any>>;
      return;
    }
    this.perfLogger = new PerformanceLogger(injector);
    this.perfScenarioCompleted$ = this.perfLogger.scenarioCompleted$;
    this.currPerfMetrics$ = this.perfScenarioCompleted$.pipe(
      filter((record) => record.scenarioName === this.perfLogger.PERFORMANCE_SCENARIO),
      map((record) => record.perfMetrics),
    );
  }

  private sessionVars: { [key: string]: any } = {};

  public init() {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    init({
      orgId: environment.fullstoryOrgId,
      host: 'fs.pencilapp.com',
      script: 'fs.pencilapp.com/s/fs.js',
      appHost: 'app.fullstory.com',
      recordOnlyThisIFrame: true,
    });

    // start perf logger
    this.perfLogger.start();

    // Store subscription for cleanup
    this.perfScenarioSubscription = this.perfLogger.scenarioCompleted$.subscribe(
      async (scenario) => {
        if (scenario.scenarioName === this.perfLogger.PERFORMANCE_SCENARIO) {
          // update last know session performance
          this.event('Performance', scenario.scenarioProperties, scenario.perfMetrics, [
            'cpu.p95',
            'fps.p95',
            'cd.p95',
            'yDocRx.p95',
            'yDocTx.p95',
          ]);
        } else {
          this.event(
            `[Scenario Profile] ${scenario.scenarioName}`,
            scenario.scenarioProperties,
            scenario.perfMetrics,
            ['duration', 'cpu.p95', 'fps.p95', 'cd.p95'],
          );
        }
      },
    );

    // Add beforeunload listener for cleanup in scenarios where ngOnDestroy might not execute
    this.beforeUnloadListener = () => {
      this.cleanup();
    };
    window.addEventListener('beforeunload', this.beforeUnloadListener);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private cleanup(): void {
    // Clean up PerformanceLogger
    if (this.perfLogger) {
      this.perfLogger.stop();
    }

    // Clean up subscriptions
    if (this.perfScenarioSubscription) {
      this.perfScenarioSubscription.unsubscribe();
    }

    // Clean up beforeunload listener
    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = undefined;
    }

    // Clean up FullStory
    this.shutdown();
  }

  public identifyUser(userData?: UserData) {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    const user = userData?.user;
    const intercom = userData?.intercom;

    if (user?._id) {
      // Identify with FullStory
      const userFSObj: any = {
        displayName: user.name,
        email: user.email,
        institution_id: user.institution?._id,
        institution_name: user.institution?.name,
        institution_admin_email: user.institution?.email ?? '',
        institution_description: user.institution?.description ?? '',
        personas: user.personas?.length ? user.personas.join(', ') : '',
        featureFlags: user.feature_flags,
        exptTrialLength: user.feature_flag_variables?.enable_billing.expt_trial_length,
        billingStatus: user.billingStatus,
        release: versions.version,
      };
      if (user.info?.roleType) {
        userFSObj.roleType = user.info.roleType;
        userFSObj.role = user.info.role;
        userFSObj.roleOther = user.info.roleOther;
        userFSObj.questionnaireComplete = user.info.questionnaireComplete;
      }
      FullStory('setIdentity', { uid: user._id, properties: userFSObj });

      // Identify with Intercom
      if (intercom?.intercomToken) {
        // intercom tracking
        const userIntercomObj: any = {
          app_id: 'keleytc7',
          email: user.email,
          user_id: user._id,
          name: user.name,
          institution_id: user.institution?._id,
          institution_name: user.institution?.name,
          personas: user.personas?.length ? user.personas.join(', ') : '',
          isAPIUser: user.isApiManaged,
          isAnonymousUser: user.isAnonymous,
          user_hash: intercom.intercomToken,
          custom_launcher_selector: '#intercomHelpLink',
          hide_default_launcher: true,
          user_release_version: versions.version,
          // Supports all optional configuration.
          widget: {
            activator: '#intercom',
          },
          language_override: user.settings?.lang_interface ?? DEFAULT_LANG_CODE,
        };
        if (user.info?.roleType) {
          userIntercomObj.roleType = user.info.roleType;
          userIntercomObj.role = user.info.role;
          userIntercomObj.roleOther = user.info.roleOther;
          userIntercomObj.questionnaireComplete = user.info.questionnaireComplete;
        }
        this.intercom.shutdown();
        this.intercom.boot(userIntercomObj);
      }
    }
  }

  public getPerfLogger() {
    return this.perfLogger;
  }

  public shutdown() {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    FullStory('shutdown');
  }

  public restart() {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    FullStory('restart');
  }

  public toggleDOMTracking(enabled: boolean) {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    // enabled = OFF => Don't track DOM
    // enabled = ON => Track DOM
    FullStory('setIdentity', { consent: enabled });
  }

  public log(logLevel: PublicLogLevel, logMessage: string) {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    FullStory('log', { level: logLevel, msg: logMessage });
  }

  public event(
    eventName: string,
    eventProperties: Record<string, any> = {},
    perfMetrics?: Record<string, any>,
    summaryKeys: (string | Record<string, string>)[] = [], // list of keys to be included in event log summary
    includeSessionAndPerf: boolean = true, // New flag, defaulting to true
  ) {
    try {
      if (this.inspectModeRepository.isInspectMode) {
        return;
      }
      // Define and initialize all three variables in one line
      let [intercomLogObject, consoleLogObject, fullstoryLogObject] = Array(3).fill({
        ...eventProperties,
      });

      if (includeSessionAndPerf) {
        if (!perfMetrics && this.perfLogger) {
          perfMetrics = this.perfLogger.getRealtimePerfMetrics();
        }

        intercomLogObject = {
          ...this.sessionVars,
          ...eventProperties,
        };

        consoleLogObject = {
          ...this.sessionVars,
          ...perfMetrics,
          ...eventProperties,
        };

        fullstoryLogObject = {
          ...consoleLogObject,
          ...this.getLegacyPerfMetrics(consoleLogObject),
        };
      }
      FullStory('trackEvent', { name: eventName, properties: fullstoryLogObject });
      this.mostRecentEventSubject.next(eventName);

      // log to Intercom (only whitelisted events)
      let logToIntercom = false;
      for (const regex of this.INTERCOM_EVENTS_WHITE_LIST) {
        if (regex.test(eventName)) {
          logToIntercom = true;
          break;
        }
      }
      if (logToIntercom) {
        this.intercom.trackEvent(eventName, intercomLogObject); // ignore perf metrics for intercom
      }

      // log Telemetry events to console for non-prod builds
      if (!environment.production) {
        const summaryStr = this.getLogSummaryStr(consoleLogObject, summaryKeys);
        const eventPropsStr = eventProperties.length
          ? `: ${JSON.stringify(eventProperties, null, 2)}`
          : '';
        console.groupCollapsed(`[Telemetry] ${eventName}${summaryStr}${eventPropsStr}`);
        console.log(`${JSON.stringify(consoleLogObject, null, 2)}`);
        console.groupEnd();
      }
      return consoleLogObject;
    } catch (e) {
      console.log('[Telemetry] Failed to log event', e);
    }
  }

  /**
   * Helper function to log events without including session vars and performance metrics.
   */
  public logBasicEvent(eventName: string, eventProperties: Record<string, any> = {}) {
    this.event(eventName, eventProperties, undefined, [], false);
  }

  public errorEvent(eventName: string, eventProperties: Record<string, any> = {}) {
    this.event(`[Error] ${eventName}`, eventProperties);
  }

  public debugEvent(eventName: string, eventProperties: Record<string, any> = {}) {
    this.event(`[Debug] ${eventName}`, eventProperties);
  }

  public setSessionVars(updatedProperties: { [key: string]: any }) {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    // Update the sessionVars with the new properties
    this.sessionVars = { ...this.sessionVars, ...updatedProperties };
    // Also udpate page properties
    FullStory('setProperties', { type: 'page', properties: this.sessionVars });
  }

  public clearSessionVars(keys: string[]) {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }

    this.sessionVars = { ...this.sessionVars };
    keys.forEach((key) => delete this.sessionVars[key]);
    FullStory('setProperties', { type: 'page', properties: this.sessionVars });
  }

  public incrementMetric(metric: CountedMetric) {
    if (!this.perfLogger) {
      return;
    }
    this.perfLogger.incrementCountedMetric(metric);
  }

  public updateMetric(metric: ExternalMetric, value: number) {
    if (!this.perfLogger) {
      return;
    }
    this.perfLogger.updateMetric(metric, value);
  }

  public startPerfScenario(scenarioName: string, scenarioProperties?: Record<string, any>): void {
    if (!this.perfLogger) {
      return;
    }
    this.perfLogger.startPerfScenario(scenarioName, scenarioProperties);
  }

  public endPerfScenario(scenarioName: string, scenarioProperties?: Record<string, any>): void {
    if (!this.perfLogger) {
      return;
    }
    this.perfLogger.endPerfScenario(scenarioName, scenarioProperties);
  }

  public setNavigatorConnectionTarget(navigatorConnectionTarget?: NavigatorConnectionTarget): void {
    if (!this.perfLogger) {
      return;
    }
    this.perfLogger.navigatorConnectionTarget = navigatorConnectionTarget;
  }

  public setNetworkEvent(networkEvent: NetworkEvent | null): void {
    if (!this.perfLogger) {
      return;
    }
    this.perfLogger.networkEvent = networkEvent;
  }

  /**
   * Logs the given object to the telemetry service.
   */
  public logObject(
    logObject: { [key: string]: any },
    logSource: string,
    logType: 'debug' | 'error' = 'debug',
  ): void {
    const logStr = Object.keys(logObject)
      .map((key) => `${key}=${JSON.stringify(logObject[key])}`)
      .join('\r\n');

    this.log(logType, `[${logSource}]\r\n${logStr}`);
  }

  private getLegacyPerfMetrics(logProperties: Record<string, any>) {
    // Maps the new keys to old keys until we have enough data in the new keys for FS dashboard
    // @TODO: stoor - remove this after 90 days of going to prod and update FS dashboards
    const legacyMetrics: Record<string, any> = {};
    legacyMetrics['cpu_p50'] = this.getObjectByKeys(logProperties, 'cpu.p50');
    legacyMetrics['cpu_p95'] = this.getObjectByKeys(logProperties, 'cpu.p95');
    legacyMetrics['cdps_p50'] = this.getObjectByKeys(logProperties, 'cd.p50');
    legacyMetrics['fps_p50'] = this.getObjectByKeys(logProperties, 'fps.p50');
    legacyMetrics['usedJSHeapSize'] = this.getObjectByKeys(logProperties, 'memory.usedJSHeapSize');
    return legacyMetrics;
  }

  private getLogSummaryStr(
    logProperties: Record<string, any>,
    summaryKeys: (string | Record<string, string>)[],
  ) {
    const summary: Record<string, any> = {};
    for (const summaryKey of summaryKeys) {
      let label: string;
      let key: string;
      if (typeof summaryKey === 'string') {
        label = summaryKey;
        key = summaryKey;
      } else {
        // (typeof summaryKey === 'object')
        [label, key] = Object.entries(summaryKey)[0];
      }
      // extract summary keys from logPropertiesWithPerfMetrics
      const val = this.getObjectByKeys(logProperties, key);
      if (val !== undefined) {
        summary[label] = val;
      }
    }
    let summaryStr = '';
    if (Object.keys(summary).length) {
      summaryStr = Object.entries(summary)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
      summaryStr = ` (${summaryStr})`;
    }
    return summaryStr;
  }

  /**
   * Can be used to get nested keys from an object using "." notation
   * const myObject = {
      level1: {
        level2: {
          level3: 'Value'
        }
      }
    };
    const result = getObjectByKeys(myObject, 'level1.level2.level3');
   */
  private getObjectByKeys(object: any, compoundKey: string) {
    return compoundKey.split('.').reduce((nestedObj, key) => {
      if (nestedObj && typeof nestedObj === 'object' && key in nestedObj) {
        return nestedObj[key];
      }
      return undefined;
    }, object);
  }
}
